import { config } from "../Environment";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface CompanionMessage {
  _id: string;
  fiUser: string;
  fiSpace: string;
  messageType:
    | "welcome"
    | "check_in"
    | "milestone"
    | "nudge"
    | "insight_reflection"
    | "pattern_observation"
    | "encouragement"
    | "weekly_summary"
    | "trait_spotlight"
    | "activity_suggestion";
  content: string;
  metadata: {
    relatedInsightIds?: string[];
    relatedActionIds?: string[];
    relatedTraitIds?: string[];
    relatedPrescriptionId?: string;
    milestoneType?: string;
    suggestedActivityId?: string;
  };
  trigger: {
    type: "time_based" | "event_based" | "pattern_based" | "manual";
    eventName?: string;
    description?: string;
  };
  isRead: boolean;
  isBookmarked: boolean;
  userReaction: "helpful" | "not_helpful" | null;
  userReactionFeedback?: string;
  createdOn: string;
}

export interface CompanionMessageListResponse {
  messages: CompanionMessage[];
  total: number;
}

// ─── API Functions ───────────────────────────────────────────────────────────

/**
 * List companion messages for a space
 */
export const listCompanionMessages = async (
  retryFetch: Function,
  spaceId: string,
  limit?: number,
  offset?: number,
  messageType?: string
): Promise<CompanionMessageListResponse> => {
  const params = new URLSearchParams();
  if (limit) params.append("limit", String(limit));
  if (offset) params.append("offset", String(offset));
  if (messageType) params.append("messageType", messageType);

  const query = params.toString() ? `?${params.toString()}` : "";
  const response = await retryFetch(
    `${config.API_BASE}/api/identity-spaces/${spaceId}/companion/messages${query}`,
    {
      method: "GET",
      credentials: "include",
    }
  );

  if (!response.ok) {
    throw new Error("Failed to fetch companion messages");
  }

  return response.json();
};

/**
 * Mark a companion message as read
 */
export const markMessageRead = async (
  retryFetch: Function,
  messageId: string
): Promise<void> => {
  const response = await retryFetch(
    `${config.API_BASE}/api/companion-messages/${messageId}/read`,
    {
      method: "PUT",
      credentials: "include",
    }
  );

  if (!response.ok) {
    throw new Error("Failed to mark message as read");
  }
};

/**
 * Set a reaction on a companion message
 */
export const setMessageReaction = async (
  retryFetch: Function,
  messageId: string,
  reaction: "helpful" | "not_helpful" | null,
  feedback?: string
): Promise<void> => {
  const body: Record<string, unknown> = { reaction };
  if (feedback !== undefined) {
    body.feedback = feedback;
  }

  const response = await retryFetch(
    `${config.API_BASE}/api/companion-messages/${messageId}/reaction`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(body),
    }
  );

  if (!response.ok) {
    throw new Error("Failed to set message reaction");
  }
};

/**
 * Manually trigger companion to generate a new message
 */
export const triggerCompanionMessage = async (
  retryFetch: Function,
  spaceId: string
): Promise<CompanionMessage> => {
  const response = await retryFetch(
    `${config.API_BASE}/api/identity-spaces/${spaceId}/companion/trigger`,
    {
      method: "POST",
      credentials: "include",
    }
  );

  if (!response.ok) {
    throw new Error("Failed to trigger companion message");
  }

  return response.json();
};
